import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API, cn, ROLE_LABELS, DEPARTMENT_LABELS, ROLE_OPTIONS, formatDate } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Search, Users, Edit, Plus, Loader2, UserPlus, Trash2, Building2 } from "lucide-react";

const ROLES = ROLE_OPTIONS;

const DEPARTMENTS = [
  { value: "merchandizing", label: "Merchandizing" },
  { value: "prepress", label: "Prepress" },
  { value: "production", label: "Production" },
  { value: "logistics", label: "Logistics" },
  { value: "packaging", label: "Packaging" },
];

export default function UsersPage() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  
  // Create user dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "team_member",
    department: "",
    phone: "",
    password: "",
    assigned_customers: [],
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/users`, { withCredentials: true }),
      axios.get(`${API}/customers`, { withCredentials: true }),
    ]).then(([usersRes, customersRes]) => {
      setUsers(usersRes.data);
      setCustomers(customersRes.data);
    }).catch((err) => console.error("Failed to fetch data:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email) {
      toast.error("Name and email are required");
      return;
    }
    if (!newUser.password || newUser.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!newUser.assigned_customers || newUser.assigned_customers.length === 0) {
      if (newUser.role !== "super_admin" && newUser.role !== "admin") {
        toast.error("At least one customer must be assigned");
        return;
      }
    }
    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/users`,
        { ...newUser, department: newUser.department || null },
        { withCredentials: true }
      );
      setUsers((prev) => [response.data, ...prev]);
      toast.success("User created successfully");
      setCreateDialogOpen(false);
      setNewUser({ name: "", email: "", role: "team_member", department: "", phone: "", password: "", assigned_customers: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleSetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setSettingPassword(true);
    try {
      await axios.post(
        `${API}/users/${userId}/set-password`,
        { password: newPassword },
        { withCredentials: true }
      );
      toast.success("Password set successfully");
      setPasswordUserId(null);
      setNewPassword("");
    } catch (err) {
      console.error("Failed to set password:", err);
      toast.error(err.response?.data?.detail || "Failed to set password");
    } finally {
      setSettingPassword(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success(`User "${userName}" deleted successfully`);
      setUsers((prev) => prev.filter((u) => u.user_id !== userId));
    } catch (err) {
      console.error("Failed to delete user:", err);
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleUpdate = async () => {
    if (!editingUser) return;
    if (!editingUser.assigned_customers || editingUser.assigned_customers.length === 0) {
      if (editingUser.role !== "super_admin" && editingUser.role !== "admin") {
        toast.error("At least one customer must be assigned");
        return;
      }
    }
    setUpdating(true);
    try {
      await axios.put(
        `${API}/users/${editingUser.user_id}`,
        {
          role: editingUser.role,
          department: editingUser.department,
          is_active: editingUser.is_active,
          assigned_customers: editingUser.assigned_customers || [],
        },
        { withCredentials: true }
      );
      setUsers((prev) =>
        prev.map((u) => u.user_id === editingUser.user_id ? editingUser : u)
      );
      toast.success("User updated successfully");
      setEditingUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setUpdating(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase())
  );

  // Group users by department for summary
  const usersByDept = users.reduce((acc, user) => {
    const dept = user.department || "unassigned";
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(user);
    return acc;
  }, {});

  if (loading) {
    return <UsersSkeleton />;
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
        <p className="text-muted-foreground">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Users</h1>
          <p className="text-muted-foreground mt-1">
            Manage user accounts, roles, and department assignments
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="create-user-button">
              <UserPlus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Create New User</DialogTitle>
              <DialogDescription>
                Add a new user to the system with role and department assignment.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  placeholder="Full name"
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                  className="rounded-sm"
                  data-testid="new-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  className="rounded-sm"
                  data-testid="new-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  placeholder="Phone number"
                  value={newUser.phone}
                  onChange={(e) => setNewUser(prev => ({ ...prev, phone: e.target.value }))}
                  className="rounded-sm"
                  data-testid="new-user-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Password <span className="text-destructive">*</span></Label>
                <Input
                  type="password"
                  placeholder="Enter password (min 6 characters)"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  className="rounded-sm"
                  data-testid="new-user-password"
                />
                <p className="text-xs text-muted-foreground">User will use this password to login</p>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="new-user-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={newUser.department || "none"}
                  onValueChange={(value) => setNewUser(prev => ({ ...prev, department: value === "none" ? "" : value }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="new-user-department">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Department</SelectItem>
                    {DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept.value} value={dept.value}>
                        {dept.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assigned Customers <span className="text-destructive">*</span></Label>
                <div className="border rounded-sm p-2 max-h-40 overflow-y-auto space-y-1">
                  {customers.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-2">No customers available</p>
                  ) : customers.map((c) => (
                    <label key={c.customer_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                      <Checkbox
                        checked={(newUser.assigned_customers || []).includes(c.customer_id)}
                        onCheckedChange={(checked) => {
                          setNewUser(prev => ({
                            ...prev,
                            assigned_customers: checked
                              ? [...(prev.assigned_customers || []), c.customer_id]
                              : (prev.assigned_customers || []).filter(id => id !== c.customer_id)
                          }));
                        }}
                        data-testid={`new-user-customer-${c.customer_id}`}
                      />
                      <span>{c.name}</span>
                      <span className="text-muted-foreground text-xs ml-auto">{c.code}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">User will only access data for assigned customers</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateUser} disabled={creating} data-testid="submit-create-user">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create User"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Department Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {DEPARTMENTS.map((dept) => {
          const count = usersByDept[dept.value]?.length || 0;
          return (
            <Card key={dept.value} className="border border-border shadow-none rounded-sm">
              <CardContent className="p-4">
                <p className="text-2xl font-heading">{count}</p>
                <p className="text-xs text-muted-foreground">{dept.label}</p>
              </CardContent>
            </Card>
          );
        })}
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="p-4">
            <p className="text-2xl font-heading">{usersByDept["unassigned"]?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Unassigned</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="user-search"
          />
        </div>
      </div>

      {/* Users Table */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  User
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Email
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Role
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Department
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Status
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Joined
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Customers
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{user.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {user.email}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs capitalize">
                      {ROLE_LABELS[user.role] || user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.department ? (
                      <Badge variant="outline" className="font-mono text-xs capitalize">
                        {DEPARTMENT_LABELS[user.department] || user.department}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={cn(
                        "font-mono text-xs",
                        user.is_active
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-50 text-slate-700"
                      )}
                    >
                      {user.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(user.created_at)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono text-xs">
                      {(user.assigned_customers || []).length} assigned
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {/* Set Password Dialog */}
                      <Dialog
                        open={passwordUserId === user.user_id}
                        onOpenChange={(open) => {
                          if (open) {
                            setPasswordUserId(user.user_id);
                          } else {
                            setPasswordUserId(null);
                            setNewPassword("");
                          }
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs"
                            data-testid={`set-password-${user.user_id}`}
                          >
                            Set Password
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="font-heading text-xl">
                              Set Password
                            </DialogTitle>
                            <DialogDescription>
                              Set a new password for {user.name}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label>New Password</Label>
                              <Input
                                type="password"
                                placeholder="Enter new password (min 6 characters)"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="rounded-sm"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setPasswordUserId(null);
                                setNewPassword("");
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleSetPassword(user.user_id)}
                              disabled={settingPassword}
                            >
                              {settingPassword ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : null}
                              Set Password
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      {/* Edit User Dialog */}
                      <Dialog
                        open={editingUser?.user_id === user.user_id}
                        onOpenChange={(open) => {
                          if (open) {
                            setEditingUser({ ...user });
                          } else {
                            setEditingUser(null);
                          }
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            data-testid={`edit-user-${user.user_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="font-heading text-xl">
                            Edit User
                          </DialogTitle>
                          <DialogDescription>
                            Update user role and department assignment.
                          </DialogDescription>
                        </DialogHeader>
                        {editingUser && (
                          <div className="space-y-4">
                            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={editingUser.picture} />
                                <AvatarFallback className="bg-primary text-primary-foreground">
                                  {editingUser.name?.charAt(0)?.toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{editingUser.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {editingUser.email}
                                </p>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label>Role</Label>
                              <Select
                                value={editingUser.role}
                                onValueChange={(value) =>
                                  setEditingUser((prev) => ({ ...prev, role: value }))
                                }
                              >
                                <SelectTrigger className="rounded-sm" data-testid="edit-role-select">
                                  <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                                <SelectContent>
                                  {ROLES.map((role) => (
                                    <SelectItem key={role.value} value={role.value}>
                                      {role.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label>Department</Label>
                              <Select
                                value={editingUser.department || "none"}
                                onValueChange={(value) =>
                                  setEditingUser((prev) => ({
                                    ...prev,
                                    department: value === "none" ? null : value,
                                  }))
                                }
                              >
                                <SelectTrigger className="rounded-sm" data-testid="edit-department-select">
                                  <SelectValue placeholder="Select department" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No Department</SelectItem>
                                  {DEPARTMENTS.map((dept) => (
                                    <SelectItem key={dept.value} value={dept.value}>
                                      {dept.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="flex items-center justify-between">
                              <Label htmlFor="is_active">Active Status</Label>
                              <Switch
                                id="is_active"
                                checked={editingUser.is_active}
                                onCheckedChange={(checked) =>
                                  setEditingUser((prev) => ({
                                    ...prev,
                                    is_active: checked,
                                  }))
                                }
                                data-testid="edit-active-switch"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label>Assigned Customers <span className="text-destructive">*</span></Label>
                              <div className="border rounded-sm p-2 max-h-40 overflow-y-auto space-y-1">
                                {customers.length === 0 ? (
                                  <p className="text-xs text-muted-foreground p-2">No customers available</p>
                                ) : customers.map((c) => (
                                  <label key={c.customer_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                                    <Checkbox
                                      checked={(editingUser.assigned_customers || []).includes(c.customer_id)}
                                      onCheckedChange={(checked) => {
                                        setEditingUser(prev => ({
                                          ...prev,
                                          assigned_customers: checked
                                            ? [...(prev.assigned_customers || []), c.customer_id]
                                            : (prev.assigned_customers || []).filter(id => id !== c.customer_id)
                                        }));
                                      }}
                                    />
                                    <span>{c.name}</span>
                                    <span className="text-muted-foreground text-xs ml-auto">{c.code}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setEditingUser(null)}>
                            Cancel
                          </Button>
                          <Button
                            onClick={handleUpdate}
                            disabled={updating}
                            data-testid="save-user-button"
                          >
                            {updating ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              "Save Changes"
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    
                    {/* Delete User */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          data-testid={`delete-user-${user.user_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete User</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{user.name}"?
                            <br /><br />
                            This action cannot be undone. Consider deactivating the user instead if you want to preserve their history.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => handleDeleteUser(user.user_id, user.name)}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No users found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const UsersSkeleton = () => (
  <div className="space-y-6">
    <Skeleton className="h-10 w-24" />
    <Skeleton className="h-10 w-full max-w-md" />
    <Skeleton className="h-96 w-full" />
  </div>
);
